import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom'
import React, { useContext, useState, useEffect } from 'react'
import type { ReactNode, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import * as Sentry from '@sentry/react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { GlobalContext } from 'GlobalContext'
import { SignIn } from 'pages/common/SignIn'
import { OAuthAuthorize } from 'pages/common/OAuthAuthorize'
import { OAuthCallback } from 'pages/common/OAuthCallback'
import { Home as HomeWeb } from 'pages/web/Home'
import { Settings as SettingsWeb } from 'pages/web/settings/Settings'
import { Timekeeper as TimekeeperWeb } from 'pages/web/timekeeper/Timekeeper'
import { UserManagement as UsersWeb } from 'pages/web/userManagement/UserManagement'
import { CustomReports as CustomReportsWeb } from 'pages/web/customReports/CustomReports'
import { Accruals } from 'pages/web/accruals/Accruals'
import { ScheduleRules as ScheduleRulesWeb } from 'pages/web/scheduleRules/ScheduleRules'
import SearchModal from 'components/SearchModal';

interface RouteErrorFallbackProps {
    error: Error;
    resetErrorBoundary: () => void;
}

const RouteErrorFallback = ({ error, resetErrorBoundary }: RouteErrorFallbackProps) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
            }}
        >
            <Stack spacing={2} sx={{ maxWidth: 560, textAlign: 'center' }}>
                <Typography variant='h5'>Ocurrio un error en esta vista</Typography>
                <Typography variant='caption' color='text.secondary'>
                    {error?.message}
                </Typography>
                <Stack direction='row' spacing={1} justifyContent='center'>
                    <Button variant='outlined' onClick={resetErrorBoundary}>
                        Reintentar
                    </Button>
                    <Button variant='contained' component={Link} to='/'>
                        Ir al inicio
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

interface RouteBoundaryProps {
    element: ReactNode;
    boundaryId: string;
}

const RouteBoundary = ({ element, boundaryId }: RouteBoundaryProps) => {
    const location = useLocation();

    return (
        <ErrorBoundary
            fallbackRender={RouteErrorFallback}
            resetKeys={[location.pathname, location.search, location.key, boundaryId]}
            onError={(error, info) => {
                console.error(`[ErrorBoundary:${boundaryId}]`, error, info);
                Sentry.captureException(error, {
                    tags: { boundaryId },
                    extra: { componentStack: info?.componentStack },
                });
            }}
        >
            {element}
        </ErrorBoundary>
    );
};

interface RedirectLocation {
    pathname?: string;
    search?: string;
    hash?: string;
}

const getSafeRedirectPath = (fromLocation?: RedirectLocation) => {
    const pathname = fromLocation?.pathname || '/';
    const search = fromLocation?.search || '';
    const hash = fromLocation?.hash || '';

    if (!pathname.startsWith('/')) {
        return '/';
    }

    if (pathname === '/signin' || pathname === '/authorize' || pathname === '/callback') {
        return '/';
    }

    return `${pathname}${search}${hash}`;
};

interface RequireAuthProps {
    element: ReactNode;
    boundaryId: string;
}

const RequireAuth = ({ element, boundaryId }: RequireAuthProps) => {
    const { authenticated } = useContext(GlobalContext);
    const location = useLocation();

    if (!authenticated) {
        return <Navigate to='/signin' replace state={{ from: location }} />;
    }

    return <RouteBoundary element={element} boundaryId={boundaryId} />;
};

const SignInRoute = () => {
    const { authenticated } = useContext(GlobalContext);
    const location = useLocation();

    if (authenticated) {
        const redirectTo = getSafeRedirectPath(location.state?.from);
        return <Navigate to={redirectTo} replace />;
    }

    return <RouteBoundary element={<SignIn />} boundaryId='signin' />;
};

const WebPaths = () => {
    const [searchOpen, setSearchOpen] = useState(false);

    const handleKeyDown = (event: KeyboardEvent) => {
        const isCommandPaletteShortcut = (event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K');

        if (isCommandPaletteShortcut) {
            event.preventDefault();
            setSearchOpen(true);
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true, v7_relativeSplatPathPrefix: true }}>
            <Routes>
                <Route
                    path='/'
                    element={<RequireAuth element={<HomeWeb />} boundaryId='home' />}
                    exact
                />

                <Route
                    path='/signin'
                    element={<SignInRoute />}
                    exact
                />

                <Route
                    path='/authorize'
                    element={<RouteBoundary element={<OAuthAuthorize />} boundaryId='oauth_authorize' />}
                    exact
                />

                <Route
                    path='/callback'
                    element={<RouteBoundary element={<OAuthCallback />} boundaryId='oauth_callback' />}
                    exact
                />

                <Route
                    path='/settings'
                    element={<RequireAuth element={<SettingsWeb />} boundaryId='settings' />}
                    exact
                />

                <Route
                    path='/settings/:subpage/*'
                    element={<RequireAuth element={<SettingsWeb />} boundaryId='settings_subpage' />}
                    exact
                />

                <Route
                    path='/timekeeper'
                    element={<RequireAuth element={<TimekeeperWeb />} boundaryId='timekeeper' />}
                    exact
                />

                <Route
                    path='/timekeeper/:subpage/*'
                    element={<RequireAuth element={<TimekeeperWeb />} boundaryId='timekeeper_subpage' />}
                    exact
                />

                <Route
                    path='/user_management'
                    element={<RequireAuth element={<UsersWeb />} boundaryId='user_management' />}
                    exact
                />

                <Route
                    path='/user_management/:subpage/*'
                    element={<RequireAuth element={<UsersWeb />} boundaryId='user_management_subpage' />}
                    exact
                />
                <Route
                    path='/custom_reports'
                    element={<RequireAuth element={<CustomReportsWeb />} boundaryId='custom_reports' />}
                    exact
                />

                <Route
                    path='/custom_reports/:subpage/*'
                    element={<RequireAuth element={<CustomReportsWeb />} boundaryId='custom_reports_subpage' />}
                    exact
                />
                <Route
                    path='/accruals'
                    element={<RouteBoundary element={<Accruals />} boundaryId='accruals' />}
                    exact
                />

                <Route
                    path='/accruals/:subpage/*'
                    element={<RouteBoundary element={<Accruals />} boundaryId='accruals_subpage' />}
                    exact
                />
                <Route
                    path='/schedule-rules'
                    element={<RequireAuth element={<ScheduleRulesWeb />} boundaryId='schedule_rules' />}
                    exact
                />

                <Route
                    path='/schedule-rules/:subpage/*'
                    element={<RequireAuth element={<ScheduleRulesWeb />} boundaryId='schedule_rules_subpage' />}
                    exact
                />

                <Route path='*' element={<>404 page not found</>} />
            </Routes>
            <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
        </Router>
    )
}

export {
    WebPaths,
}